import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { apiJson, type HttpError } from './request-service';
import type { AccessMap } from '../pages/customers/access-map';
import { useCurrentUserContext } from './current-user-context';
import { sortParams, type SortState } from './table-sort';

/**
 * Recipe step 3 — gate each FETCH on the tab the server gates it on.
 *
 * ⚠️ NOT one id: this file serves the customer's Orders LIST and three tabs on
 * the order DETAIL page, and they are separate destinations.
 * `useOrderLookups` is deliberately absent — shared reference data, still on
 * the old gate, and every caller of this file holds one of the tabs below.
 */
const CUSTOMER_ORDERS_TAB   = 'customers.detail.orders';
const ORDER_INVOICE_TAB     = 'orders.detail.invoice';
const ORDER_TIMELINE_TAB    = 'orders.detail.timeline';
const ORDER_COMMISSIONS_TAB = 'orders.detail.commissions';
import {
  type CustomerSource,
  DEFAULT_SOURCE,
  customersApiBase,
  ordersApiBase,
} from './customer-source';

// API response shapes — snake_case to match what the api returns from
// app.orders_v1 / app.order_lines_v1 / app.order_timeline_v1. No
// client-side renaming.

export type OrderListRow = {
  order_id:                    number;
  customer_id:                 number;
  // #1268 — owning customer's name, from the `app.customers` join the list
  // query already makes. Rendered as the Name column beside Customer.
  first_name:                  string;
  last_name:                   string;
  // The owning customer's country, from the same join as the name. NOT NULL in
  // `app.customers`, but it can be the empty string — the column renders a dash
  // rather than an empty cell for those.
  main_country:                string;
  order_date:                  string; // ISO datetime
  total:                       string; // numeric — comes through as string
  currency_code:               string;
  business_volume_total:       string;
  commissionable_volume_total: string;
  order_status:                string | null;
  order_type:                  string | null;
};

/**
 * Order header — projection of `SELECT *` from `app.orders_v1`. The mirror
 * carries ~100 columns; the SPA picks what to render. Adds optional
 * `dx_version` / `dx_updated_at` / `dx_updated_by` optimistic-lock fields.
 */
export type OrderHeader = {
  /**
   * Per-field decisions from the server (Q13). Every order column is `open`
   * today, so nothing masks — but this still carries the EDIT answer, which is
   * what the Edit button derives from.
   */
  _access?: AccessMap;
  order_id:        number;
  customer_id:     number;
  order_date:      string;
  order_status_id: number;
  order_status:    string | null;
  order_type_id:   number;
  order_type:      string | null;
  currency_code:               string;
  total:                       string;
  sub_total:                   string;
  tax_total:                   string;
  shipping_total:              string;
  discount_total:              string;
  discount_percent:            string;
  business_volume_total:       string;
  commissionable_volume_total: string;
  first_name:  string;
  middle_name: string;
  last_name:   string;
  name_suffix: string;
  company:     string;
  address1:    string;
  address2:    string;
  address3:    string;
  city:        string;
  state:       string;
  zip:         string;
  country:     string;
  county:      string;
  email:       string;
  phone:       string;
  notes:       string;
  tracking_number1: string;
  tracking_number2: string;
  tracking_number3: string;
  tracking_number4: string;
  tracking_number5: string;
  created_date:  string;
  modified_date: string;
  created_by:    string;
  modified_by:   string;
  shipped_date:  string | null;
  locked_date:   string | null;
  /**
   * When the acceptance pipeline stamped this order as commissioned by an
   * accepted run (`app.orders.commissioned_date`, projected by `app.orders_v1`).
   * NULL for an order no accepted run has commissioned.
   */
  commissioned_date: string | null;
  is_commissionable: boolean;
  return_order_id: number | null;
  replacement_order_id: number | null;
  // Exigo order custom-field slots. Authoritative names come from
  // `dbo.OrderFieldNames`; display labels live in `pages/customers/order-field-labels.ts`.
  // other13 is unnamed in Exigo (pending #859).
  other11: string | null;
  other12: string | null;
  other13: string | null;
  other14: string | null;
  other15: string | null;
  other16: string | null;
  other17: string | null;
  other18: string | null;
  other19: string | null;
  other20: string | null;
  _synced_at:  string;
  _deleted_at: string | null;
  dx_version?:    number;
  dx_updated_at?: string | null;
  dx_updated_by?: string | null;
  // Catch-all for the ~50 columns the explicit list omits (other1_total..,
  // *_tax_total, is_commissionable, parent_order_id, etc.). The Raw tab
  // renders these generically.
  [extra: string]: unknown;
};

export type OrderLine = {
  order_id:               number;
  order_line:             number;
  order_detail_id:        string | null;
  parent_order_detail_id: string | null;
  item_id:                number;
  item_code:              string;
  item_description:       string;
  quantity:               string;
  price_each:             string;
  price_total:            string;
  tax:                    string;
  business_volume:        string;
  business_volume_each:   string;
  commissionable_volume:  string;
  commissionable_volume_each: string;
  reference1:             string | null;
  dx_version?:    number;
  dx_updated_at?: string | null;
  dx_updated_by?: string | null;
  [extra: string]: unknown;
};

/** An accepted monthly period, as the order detail GET names it. */
export type ClosedPeriod = { period_id: number; period_description: string };

export type Order = {
  order:      OrderHeader;
  line_items: OrderLine[];
  /**
   * Per-field access for the order-LINE edit (`order_details` section). Kept
   * apart from `order._access` because the two sections share column names.
   */
  line_access?: AccessMap;
  /**
   * The accepted monthly period this order is in, or null (#1619, #1620).
   * When set, the order's commission inputs — Is Commissionable and every line
   * input — are locked: the server refuses them with a 422.
   */
  closed_period?: ClosedPeriod | null;
};

export type OrderTimelineEvent = {
  event_id:    number;
  order_id:    number;
  event_type:  'change' | 'status' | 'edit';
  event_date:  string | null;
  actor:       string;
  actor_label: string | null;
  payload:     string | null;
  to_id:       number | null;
  from_id:     number | null;
};

export type OrderLookups = {
  statuses: { id: number; name: string }[];
  types:    { id: number; name: string }[];
};

/**
 * One page of a customer's orders.
 *
 * ⚠️ `pageSize` is what the REQUEST asked for — the SPA asks for a BLOCK of ten
 * pages and slices one out client-side — and `hasMore` answers "is there
 * another BLOCK?". Neither is the page-level answer a pager needs; see
 * `app-kit/block-paging.ts`.
 */
export type OrdersPage = {
  rows:     OrderListRow[];
  page:     number;
  pageSize: number;
  hasMore:  boolean;
};

/**
 * One row of the Orders-tab CSV export (#1412).
 *
 * Identical to `OrderListRow` since #1443 put Country on the table — country
 * was the last field the export carried that the screen did not. Mirrors
 * `OrderExportRow` in the api (routes/orders-types.ts).
 */
export type OrderExportRow = OrderListRow;

/** #1215 — per-column filter values for the orders table header row. */
export type OrderColumnFilters = {
  orderId?:  string;
  custId?:   string;
  /** Owning customer's name — substring, matched against "first last". */
  name?:     string;
  /**
   * Owning customer's country — EXACT, from the select the facets route fills.
   * Not a substring: the value is chosen, never typed (#1443).
   */
  country?:  string;
  statusId?: string;
  typeId?:   string;
  since?:    string; // date-only, from the Order Date column
  until?:    string;
};

/**
 * ⚠️ The querystring is built by ITERATING this list, so a filter missing here
 * is silently never sent: the input works, the request omits it, and the table
 * shows the unfiltered list with no error anywhere.
 */
export const ORDER_COLUMN_FILTER_KEYS: (keyof OrderColumnFilters)[] = [
  'orderId', 'custId', 'name', 'country', 'statusId', 'typeId', 'since', 'until',
];

/** The filter querystring both the rows query and the count query send. */
/**
 * Orders-tab click-to-sort keys — admin-api `ORDER_LIST_SORT` (the column
 * keys). Default newest order first: the Order ID column.
 */
export const ORDER_LIST_SORTS = [
  'order_id', 'customer_id', 'customer_name', 'main_country', 'order_date',
  'order_status', 'order_type', 'business_volume_total', 'commissionable_volume_total',
] as const;
export type OrderListSort = (typeof ORDER_LIST_SORTS)[number];
export const ORDER_LIST_DEFAULT_SORT: SortState<OrderListSort> = { key: 'order_id', dir: 'desc' };

/** `sort` / `dir` for the server — none for the default order. */
function orderSortParams(sort: SortState<OrderListSort> | undefined): Record<string, string> {
  if (!sort || (sort.key === ORDER_LIST_DEFAULT_SORT.key && sort.dir === ORDER_LIST_DEFAULT_SORT.dir)) return {};
  return sortParams(sort);
}
const sortKeyOf = (sort: SortState<OrderListSort> | undefined) => {
  const p = orderSortParams(sort);
  return `${p.sort ?? ''}:${p.dir ?? ''}`;
};

function orderFilterParams(colFilters: OrderColumnFilters, since?: string, until?: string) {
  const qs = new URLSearchParams();
  if (since) qs.set('since', since);
  if (until) qs.set('until', until);
  for (const k of ORDER_COLUMN_FILTER_KEYS) {
    const v = (colFilters[k] ?? '').trim();
    if (v.length > 0) qs.set(k, v);
  }
  return qs;
}

/** Stable cache key for a filter set — one joined string keeps the query-key
 *  tuple flat (same approach as useCustomersList). */
function orderFilterKey(colFilters: OrderColumnFilters, since?: string, until?: string) {
  return `${ORDER_COLUMN_FILTER_KEYS
    .map((k) => `${k}=${(colFilters[k] ?? '').trim()}`)
    .join('&')}&_since=${since ?? ''}&_until=${until ?? ''}`;
}

/**
 * One page (in practice one BLOCK) of a customer's orders. The list endpoint
 * lives under the customers tree (`/api/customers/:id/orders`), so this hook
 * reuses `customersApiBase`.
 *
 * Paged the same way as the downline (`page` + `pageSize` + `hasMore`, with the
 * total arriving separately from `useCustomerOrdersCount`) — counting the scope
 * costs ~1.6s on the largest partner and must not be paid per page turn.
 */
export function useCustomerOrders(opts: {
  customerId: string | number | null;
  since?:     string; // YYYY-MM-DD, an America/Chicago calendar day; the range includes both ends
  until?:     string;
  source?:    CustomerSource;
  colFilters?: OrderColumnFilters;
  /** 1-based page number, in units of `pageSize`. */
  page?:      number;
  pageSize?:  number;
  enabled?:   boolean;
  /** Click-to-sort; omitted = newest order first. */
  sort?:      SortState<OrderListSort>;
}) {
  const mayReach = useCurrentUserContext().reachesTab(CUSTOMER_ORDERS_TAB);
  const { instance } = useMsal();
  const customerId = opts.customerId;
  const source     = opts.source ?? DEFAULT_SOURCE;
  const apiBase    = customersApiBase(source);
  const colFilters = opts.colFilters ?? {};
  const page       = opts.page     ?? 1;
  const pageSize   = opts.pageSize ?? 50;
  const colKey     = orderFilterKey(colFilters, opts.since, opts.until);
  return useQuery<OrdersPage, HttpError>({
    queryKey: ['orders', source, customerId, colKey, page, pageSize, sortKeyOf(opts.sort)],
    queryFn: () => {
      const qs = orderFilterParams(colFilters, opts.since, opts.until);
      qs.set('page', String(page));
      qs.set('pageSize', String(pageSize));
      for (const [k, v] of Object.entries(orderSortParams(opts.sort))) qs.set(k, v);
      return apiJson(
        instance,
        `${apiBase}/${encodeURIComponent(String(customerId))}/orders?${qs.toString()}`,
      );
    },
    // Recipe step 3 — gated on the Orders TAB, not just on having a customer.
    enabled: (opts.enabled ?? true) && mayReach && customerId != null && String(customerId).length > 0,
    staleTime: 30_000,
    // Keep the previous block on screen while a page turn is in flight, so the
    // table does not blank out between pages.
    placeholderData: (prev) => prev,
  });
}

/**
 * Total orders matching the CURRENT filters, for the page count and the
 * last-page jump.
 *
 * ⚠️ Must be given the SAME filters as the rows query, or the pager is sized
 * from a different result set than it pages through. Deliberately NOT given
 * `page` — the total is the same for every page of one filter set.
 */
export function useCustomerOrdersCount(opts: {
  customerId: string | number | null;
  since?:     string;
  until?:     string;
  source?:    CustomerSource;
  colFilters?: OrderColumnFilters;
  enabled?:   boolean;
}) {
  const mayReach = useCurrentUserContext().reachesTab(CUSTOMER_ORDERS_TAB);
  const { instance } = useMsal();
  const customerId = opts.customerId;
  const source     = opts.source ?? DEFAULT_SOURCE;
  const apiBase    = customersApiBase(source);
  const colFilters = opts.colFilters ?? {};
  const colKey     = orderFilterKey(colFilters, opts.since, opts.until);
  return useQuery<{ total: number }, HttpError>({
    queryKey: ['orders', source, 'count', customerId, colKey],
    queryFn: () => {
      const qs = orderFilterParams(colFilters, opts.since, opts.until);
      const suffix = qs.toString();
      return apiJson(
        instance,
        `${apiBase}/${encodeURIComponent(String(customerId))}/orders/count${suffix ? `?${suffix}` : ''}`,
      );
    },
    enabled: (opts.enabled ?? true) && mayReach && customerId != null && String(customerId).length > 0,
    staleTime: 30_000,
  });
}

/**
 * Distinct countries in this customer's order scope, for the Country filter's
 * select (#1443).
 *
 * ⚠️ Takes NO filters, and the query key holds none: the option list is the
 * scope's countries and does not narrow as the operator filters. So this is one
 * fetch per customer, cached for the visit — not a request per keystroke.
 *
 * Cheap on the server (it reads `app.customers`, never the orders), so unlike
 * the downline's facets — which cost ~730ms and are fetched only when the row
 * list was truncated — this one just runs.
 */
export function useCustomerOrdersFacets(opts: {
  customerId: string | number | null;
  source?:    CustomerSource;
  enabled?:   boolean;
}) {
  const mayReach = useCurrentUserContext().reachesTab(CUSTOMER_ORDERS_TAB);
  const { instance } = useMsal();
  const customerId = opts.customerId;
  const source     = opts.source ?? DEFAULT_SOURCE;
  const apiBase    = customersApiBase(source);
  return useQuery<{ countries: string[] }, HttpError>({
    queryKey: ['orders', source, 'facets', customerId],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(customerId))}/orders/facets`,
    ),
    enabled: (opts.enabled ?? true) && mayReach && customerId != null && String(customerId).length > 0,
    // The scope's countries change when the partner enrols someone abroad —
    // not within a visit. Longer than the 30s the rows use, for that reason.
    staleTime: 5 * 60_000,
  });
}

/** The orders either side of one order in the Orders-tab list. */
export type OrderNeighbours = {
  /** The row above in the list (newer), or null at the top. */
  prev: number | null;
  /** The row below in the list (older), or null at the bottom. */
  next: number | null;
};

/** Query-key prefix covering every `useOrderNeighbours` entry for a source. */
export const ORDER_NEIGHBOURS_KEY = (source: CustomerSource) => ['orders', source, 'neighbours'] as const;

/**
 * Previous / Next for the order detail page (#1512) — the orders either side of
 * `orderId` in `customerId`'s Orders tab, under the SAME filters the list was
 * showing. Answered by the server because the list only ever holds one block
 * of rows, so the neighbour may not have been fetched.
 *
 * Gated on the Orders TAB, like the route: the answer reveals the list.
 */
export function useOrderNeighbours(opts: {
  customerId: string | number | null;
  orderId:    string | number | null;
  source?:    CustomerSource;
  colFilters?: OrderColumnFilters;
  /** The list's sort — Previous / Next walk the list in that order. */
  sort?:      SortState<OrderListSort>;
}) {
  const mayReach = useCurrentUserContext().reachesTab(CUSTOMER_ORDERS_TAB);
  const { instance } = useMsal();
  const { customerId, orderId } = opts;
  const source     = opts.source ?? DEFAULT_SOURCE;
  const apiBase    = customersApiBase(source);
  const colFilters = opts.colFilters ?? {};
  const colKey     = orderFilterKey(colFilters);
  return useQuery<OrderNeighbours, HttpError>({
    // ⚠️ `ORDER_NEIGHBOURS_KEY` is a prefix of this — Edit order invalidates by
    // it, because an edit can move an order into or out of the filtered list.
    queryKey: [...ORDER_NEIGHBOURS_KEY(source), customerId, orderId, colKey, sortKeyOf(opts.sort)],
    queryFn: () => {
      const qs = orderFilterParams(colFilters);
      for (const [k, v] of Object.entries(orderSortParams(opts.sort))) qs.set(k, v);
      const suffix = qs.toString();
      return apiJson(
        instance,
        `${apiBase}/${encodeURIComponent(String(customerId))}/orders/` +
        `${encodeURIComponent(String(orderId))}/neighbours${suffix ? `?${suffix}` : ''}`,
      );
    },
    enabled: mayReach
      && customerId != null && String(customerId).length > 0
      && orderId != null && String(orderId).length > 0,
    staleTime: 30_000,
  });
}

/** Single order — header + all line items in one request. */
/**
 * Fetch the WHOLE filtered order list for a CSV export, capped server-side
 * (#1412).
 *
 * Not a hook: an export is an event, not state. Called on click so nothing is
 * fetched until the operator asks — the response can be 50,000 rows.
 *
 * ⚠️ Sends the SAME filter params the table and the count send, through the
 * same builder. A separate querystring here would produce a file that
 * disagrees with the screen it was launched from.
 */
export async function fetchCustomerOrdersExport(
  instance: ReturnType<typeof useMsal>['instance'],
  customerId: string | number,
  opts: {
    source?:     CustomerSource;
    colFilters?: OrderColumnFilters;
    since?:      string;
    until?:      string;
    /** The list's sort, so the file reads in the order on screen. */
    sort?:       SortState<OrderListSort>;
  } = {},
): Promise<{ rows: OrderExportRow[]; truncated: boolean; maxRows: number }> {
  const apiBase = customersApiBase(opts.source ?? DEFAULT_SOURCE);
  const params = orderFilterParams(opts.colFilters ?? {}, opts.since, opts.until);
  for (const [k, v] of Object.entries(orderSortParams(opts.sort))) params.set(k, v);
  const qs = params.toString();
  return apiJson(
    instance,
    `${apiBase}/${encodeURIComponent(String(customerId))}/orders/export${qs ? `?${qs}` : ''}`,
  );
}

export function useOrder(orderId: string | number | null, opts: { source?: CustomerSource } = {}) {
  const mayReach = useCurrentUserContext().reachesTab(ORDER_INVOICE_TAB);
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = ordersApiBase(source);
  return useQuery<Order, HttpError>({
    queryKey: ['order', source, 'detail', orderId],
    queryFn: () => apiJson(instance, `${apiBase}/${encodeURIComponent(String(orderId))}`),
    enabled: mayReach && orderId != null && String(orderId).length > 0,
    staleTime: 60_000,
  });
}

/**
 * Editable order-header projection + `dx_version` for the order edit modal.
 * Backed by `GET /api/customers/:customerId/orders/:orderId/edit` (reads
 * app.orders directly). `order_date` is normalised to `YYYY-MM-DD` so the
 * date input seeds correctly.
 */
export type OrderEditFields = {
  order_id:                    number;
  customer_id:                 number;
  order_date:                  string;
  order_status_id:             number;
  notes:                       string;
  other12:                     string | null;
  other15:                     string | null;
  is_commissionable:           boolean;
  commissionable_volume_total: string;
  business_volume_total:       string;
  dx_version:                  number;
};

export function useOrderEdit(
  customerId: string | number | null,
  orderId:    string | number | null,
) {
  const mayReach = useCurrentUserContext().reachesTab(ORDER_INVOICE_TAB);
  const { instance } = useMsal();
  return useQuery<OrderEditFields, HttpError>({
    queryKey: ['order', 'edit', customerId, orderId],
    queryFn: async () => {
      const row = await apiJson<OrderEditFields>(
        instance,
        `/api/customers/${encodeURIComponent(String(customerId))}/orders/${encodeURIComponent(String(orderId))}/edit`,
      );
      return { ...row, order_date: row.order_date ? String(row.order_date).slice(0, 10) : '' };
    },
    enabled: mayReach && customerId != null && orderId != null && String(orderId).length > 0,
    staleTime: 0,
  });
}

/** The line fields the order-line edit reads and shows. */
export type EditableOrderLine = Pick<OrderLine,
  | 'order_id' | 'order_line' | 'item_code' | 'item_description'
  | 'quantity' | 'business_volume_each' | 'business_volume'
  | 'commissionable_volume_each' | 'commissionable_volume'>;

/**
 * The order's `dx_version` and its lines, read in ONE snapshot, for the
 * order-line edit modal. The version is the lock for the lines, so the modal
 * must not pair it with the (up to a minute stale) lines on the detail page.
 * Backed by `GET /api/customers/:customerId/orders/:orderId/lines/edit`.
 */
export type OrderLinesEditSnapshot = {
  dx_version: number;
  line_items: EditableOrderLine[];
};

export function useOrderLinesEdit(
  customerId: string | number | null,
  orderId:    string | number | null,
) {
  const mayReach = useCurrentUserContext().reachesTab(ORDER_INVOICE_TAB);
  const { instance } = useMsal();
  return useQuery<OrderLinesEditSnapshot, HttpError>({
    queryKey: ['order', 'lines-edit', customerId, orderId],
    queryFn: () => apiJson(
      instance,
      `/api/customers/${encodeURIComponent(String(customerId))}/orders/${encodeURIComponent(String(orderId))}/lines/edit`,
    ),
    enabled: mayReach && customerId != null && orderId != null && String(orderId).length > 0,
    staleTime: 0,
  });
}

/**
 * Per-order timeline. Mirrors the customer-timeline pattern — limit-only,
 * sorted newest-first. Default 200 events; bump via `limit` up to 500 for
 * orders with unusually long change-log history.
 */
export function useOrderTimeline(
  orderId: string | number | null,
  opts: { source?: CustomerSource; limit?: number } = {},
) {
  const mayReach = useCurrentUserContext().reachesTab(ORDER_TIMELINE_TAB);
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const limit  = opts.limit  ?? 200;
  const apiBase = ordersApiBase(source);
  return useQuery<{ rows: OrderTimelineEvent[] }, HttpError>({
    queryKey: ['order', source, 'timeline', orderId, limit],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(orderId))}/timeline?limit=${limit}`,
    ),
    enabled: mayReach && orderId != null && String(orderId).length > 0,
    staleTime: 30_000,
  });
}

/**
 * Order statuses + types for the timeline status-event labels and any
 * filter UI. Lookup tables stay in `exigo_mirror` even for demo source —
 * `source` keys the cache slot only.
 */
export function useOrderLookups(opts: { source?: CustomerSource } = {}) {
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = ordersApiBase(source);
  return useQuery<OrderLookups, HttpError>({
    queryKey: ['order', source, 'lookups'],
    queryFn: () => apiJson(instance, `${apiBase}/lookups`),
    staleTime: 5 * 60_000,
  });
}

/**
 * One commission payment to one earner on an order (#859).
 *
 * One row per `app.commission_details` row — deliberately not rolled up by
 * bonus. `level` is what Exigo makes an operator page through as PB1..PB5.
 */
export type OrderCommissionRow = {
  commission_run_id:          number;
  commission_run_description: string | null;
  accepted_date:              string | null;
  bonus_id:                   number;
  bonus_name:                 string;
  /** Exigo operator-facing type code (PB1..PB5, SP, POB, …); null when undecoded. */
  bonus_type:                 string | null;
  customer_id:                number;
  customer_name:              string;
  rank_code:                  string;
  level:                      number | null;
  paid_level:                 number | null;
  percentage:                 number | null;
  /** The line's own (local) currency — what `amount` is denominated in. */
  currency_code:              string;
  /** Commission in the line's local currency. */
  amount:                     number;
  /** The same commission in USD (`entry_commission_amount`) — what Exigo shows. */
  amount_usd:                 number;
  /** The ORDER's currency — what `amount_order` is denominated in. */
  order_currency_code:        string;
  /** The same commission in the order's currency; null when the run has no rate for it. */
  amount_order:               number | null;
  /** The base the percentage is paid on, in the order's currency; null when the run has no rate for it. */
  base_order:                 number | null;
};

/**
 * Commission payouts on an order, across every ACCEPTED run.
 *
 * Empty is a legitimate result, not an error: an order in the current open
 * period has no accepted run yet (#859). Requires `commissions:read`, which
 * some roles holding `customers:read` do not have — a 403 here means the
 * operator can see the order but not what it paid.
 */
export function useOrderCommissions(
  orderId: string | number | null,
  opts: { source?: CustomerSource; enabled?: boolean } = {},
) {
  const mayReach = useCurrentUserContext().reachesTab(ORDER_COMMISSIONS_TAB);
  const { instance } = useMsal();
  const source = opts.source ?? DEFAULT_SOURCE;
  const apiBase = ordersApiBase(source);
  return useQuery<{ rows: OrderCommissionRow[] }, HttpError>({
    queryKey: ['order', source, 'commissions', orderId],
    queryFn: () => apiJson(
      instance,
      `${apiBase}/${encodeURIComponent(String(orderId))}/commissions`,
    ),
    enabled: mayReach && (opts.enabled ?? true) && orderId != null && String(orderId).length > 0,
    staleTime: 60_000,
  });
}
